/**
 * Customer-facing site-time formatting.
 *
 * Authoritative timezone is sites.timezone (IANA) from site metadata/API.
 * Never use the browser timezone for operational site data.
 *
 * TIMESTAMP/INSTANT: convert UTC/offset timestamps into the site IANA zone.
 * DATE-ONLY: YYYY-MM-DD is a calendar date and must not timezone-shift.
 * Missing site timezone: render explicit UTC.
 */

export const UTC_TIME_ZONE = 'UTC';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const NAIVE_LOCAL_INSTANT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;
const RFC1123_GMT_RE = /^(?:[A-Za-z]{3},?\s+)?\d{1,2}\s+[A-Za-z]{3}\s+\d{4}\s+\d{2}:\d{2}(?::\d{2})?\s+(?:GMT|UTC)$/i;

export type SiteTimeZoneSource = {
  timezone?: string | null;
  site_timezone?: string | null;
  timeZone?: string | null;
} | string | null | undefined;

export function isCalendarDateOnly(value: unknown): boolean {
  return DATE_ONLY_RE.test(String(value || '').trim());
}

export function resolveSiteTimeZone(source?: SiteTimeZoneSource): string | null {
  if (typeof source === 'string') {
    const text = source.trim();
    return text && isValidIanaTimeZone(text) ? text : null;
  }
  const candidates = [
    source?.timezone,
    source?.site_timezone,
    source?.timeZone,
  ];
  for (const candidate of candidates) {
    const text = String(candidate || '').trim();
    if (text && isValidIanaTimeZone(text)) return text;
  }
  return null;
}

export function isValidIanaTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function resolvedFormatTimeZone(timeZone?: string | null): { timeZone: string; explicitUtc: boolean } {
  const resolved = resolveSiteTimeZone(timeZone);
  if (resolved) return { timeZone: resolved, explicitUtc: false };
  return { timeZone: UTC_TIME_ZONE, explicitUtc: true };
}

function looksLikeIsoInstant(value: string): boolean {
  const text = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}T/.test(text)) return false;
  return !isCalendarDateOnly(text);
}

function looksLikeRfc1123Gmt(value: string): boolean {
  return RFC1123_GMT_RE.test(value.trim());
}

function parseInstant(value: string): Date | null {
  const text = value.trim();
  if (!text) return null;
  if (looksLikeIsoInstant(text)) {
    const normalized = NAIVE_LOCAL_INSTANT_RE.test(text) ? `${text}Z` : text;
    const parsed = Date.parse(normalized);
    if (Number.isFinite(parsed)) return new Date(parsed);
    return null;
  }
  if (looksLikeRfc1123Gmt(text)) {
    const parsed = Date.parse(text.replace(/\bUTC\b/i, 'GMT'));
    if (Number.isFinite(parsed)) return new Date(parsed);
  }
  return null;
}

function formatWithParts(
  date: Date,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat('en-US', { timeZone, ...options }).format(date);
}

function appendUtcIfNeeded(label: string, explicitUtc: boolean): string {
  if (!explicitUtc) return label;
  if (/\bUTC\b/i.test(label)) return label;
  return `${label} UTC`;
}

export function formatSiteDate(
  value?: string | null,
  timeZone?: string | null,
): string {
  const text = String(value || '').trim();
  if (!text) return '';
  if (isCalendarDateOnly(text)) {
    const [year, month, day] = text.split('-').map(Number);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: UTC_TIME_ZONE,
    }).format(new Date(Date.UTC(year, month - 1, day)));
  }
  const instant = parseInstant(text);
  if (!instant) return text;
  const resolved = resolvedFormatTimeZone(timeZone);
  return appendUtcIfNeeded(
    formatWithParts(instant, resolved.timeZone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    resolved.explicitUtc,
  );
}

export function formatSiteTime(
  value?: string | null,
  timeZone?: string | null,
): string {
  const text = String(value || '').trim();
  if (!text) return '';
  if (isCalendarDateOnly(text)) return formatSiteDate(text, timeZone);
  const instant = parseInstant(text);
  if (!instant) return text;
  const resolved = resolvedFormatTimeZone(timeZone);
  return appendUtcIfNeeded(
    formatWithParts(instant, resolved.timeZone, {
      hour: 'numeric',
      minute: '2-digit',
    }),
    resolved.explicitUtc,
  );
}

export function formatSiteDateTime(
  value?: string | null,
  timeZone?: string | null,
  options?: { includeYear?: boolean; includeSeconds?: boolean; includeTimeZoneName?: boolean },
): string {
  const text = String(value || '').trim();
  if (!text) return '';
  if (isCalendarDateOnly(text)) return formatSiteDate(text, timeZone);
  const instant = parseInstant(text);
  if (!instant) return text;
  const resolved = resolvedFormatTimeZone(timeZone);
  const includeYear = options?.includeYear !== false;
  const formatted = formatWithParts(instant, resolved.timeZone, {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' as const } : {}),
    hour: 'numeric',
    minute: '2-digit',
    ...(options?.includeSeconds ? { second: '2-digit' as const } : {}),
    ...(options?.includeTimeZoneName && !resolved.explicitUtc
      ? { timeZoneName: 'short' as const }
      : {}),
  });
  return appendUtcIfNeeded(formatted.replace(/\bGMT\b/g, 'UTC'), resolved.explicitUtc);
}

export function formatSiteDateTimeRange(
  start?: string | null,
  end?: string | null,
  timeZone?: string | null,
): string {
  const from = formatSiteDateTime(start, timeZone);
  const to = formatSiteDateTime(end, timeZone);
  if (from && to) return `${from} → ${to}`;
  return from || to || '';
}

function splitClockPeriod(label: string): { clock: string; period: string; utc: boolean } {
  const utc = /\s+UTC$/i.test(label);
  const core = label.replace(/\s+UTC$/i, '').trim();
  const match = core.match(/^(.*)\s+(AM|PM)$/i);
  return {
    clock: match ? match[1] : core,
    period: match ? match[2].toUpperCase() : '',
    utc,
  };
}

/**
 * Compact site-local clock range for operational windows.
 * Same-day 15-minute windows render as "7:30–7:45 PM" (no UTC, no browser zone).
 * Cross-day ranges keep each side's site date.
 */
export function formatSiteTimeRange(
  start?: string | null,
  end?: string | null,
  timeZone?: string | null,
): string {
  const fromTime = formatSiteTime(start, timeZone);
  const toTime = formatSiteTime(end, timeZone);
  if (!fromTime && !toTime) return '';
  if (!fromTime) return toTime;
  if (!toTime) return fromTime;
  const fromDay = formatSiteDate(start, timeZone);
  const toDay = formatSiteDate(end, timeZone);
  if (fromDay && toDay && fromDay !== toDay) {
    const fromDateTime = formatSiteDateTime(start, timeZone, { includeYear: false });
    const toDateTime = formatSiteDateTime(end, timeZone, { includeYear: false });
    if (fromDateTime && toDateTime) return `${fromDateTime}–${toDateTime}`;
  }
  const from = splitClockPeriod(fromTime);
  const to = splitClockPeriod(toTime);
  const utc = from.utc || to.utc;
  if (from.period && from.period === to.period) {
    const label = `${from.clock}–${to.clock} ${from.period}`;
    return utc ? `${label} UTC` : label;
  }
  const fromCore = fromTime.replace(/\s+UTC$/i, '').trim();
  const toCore = toTime.replace(/\s+UTC$/i, '').trim();
  const label = `${fromCore}–${toCore}`;
  return utc ? `${label} UTC` : label;
}

/** Month + day only, for compact Overview control dates. Date-only never shifts. */
export function formatSiteMonthDay(
  value?: string | null,
  timeZone?: string | null,
): string {
  const text = String(value || '').trim();
  if (!text) return '';
  if (isCalendarDateOnly(text)) {
    const [year, month, day] = text.split('-').map(Number);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: UTC_TIME_ZONE,
    }).format(new Date(Date.UTC(year, month - 1, day)));
  }
  const instant = parseInstant(text);
  if (!instant) return text;
  const resolved = resolvedFormatTimeZone(timeZone);
  return appendUtcIfNeeded(
    formatWithParts(instant, resolved.timeZone, {
      month: 'short',
      day: 'numeric',
    }),
    resolved.explicitUtc,
  );
}

/** `datetime-local` input value in the site zone (never browser zone). */
export function formatSiteDateTimeLocalInput(
  value?: string | null,
  timeZone?: string | null,
): string {
  const text = String(value || '').trim();
  if (!text) return '';
  if (isCalendarDateOnly(text)) return `${text}T00:00`;
  const instant = parseInstant(text);
  if (!instant) return '';
  const resolved = resolvedFormatTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: resolved.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes) => (
    parts.find((part) => part.type === type)?.value || ''
  );
  return `${read('year')}-${read('month')}-${read('day')}T${read('hour')}:${read('minute')}`;
}

/** Parse a `datetime-local` value as site-local time and return a UTC ISO instant. */
export function parseSiteDateTimeLocalInput(
  localValue?: string | null,
  timeZone?: string | null,
): string | undefined {
  const text = String(localValue || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text)) return undefined;
  const naive = text.slice(0, 16);
  const resolved = resolvedFormatTimeZone(timeZone);
  const utcGuess = Date.parse(`${naive}:00Z`);
  if (!Number.isFinite(utcGuess)) return undefined;
  let instant = utcGuess;
  for (let i = 0; i < 4; i += 1) {
    const actual = formatSiteDateTimeLocalInput(new Date(instant).toISOString(), resolved.timeZone);
    if (actual === naive) return new Date(instant).toISOString();
    const actualMs = Date.parse(`${actual}:00Z`);
    if (!Number.isFinite(actualMs)) break;
    instant += utcGuess - actualMs;
  }
  return new Date(instant).toISOString();
}

/**
 * Start of the currently OPEN site-local PT15M wall-clock window.
 * Floors in the site IANA zone (DST-safe), never from latestQualifiedAt or OM.
 */
export function openSitePt15mWindowStart(
  asOf: string | Date | null | undefined,
  timeZone?: string | null,
): string | null {
  const instant = asOf instanceof Date ? asOf.toISOString() : String(asOf || '').trim();
  if (!instant) return null;
  const local = formatSiteDateTimeLocalInput(instant, timeZone);
  if (!local) return null;
  const [datePart, timePart] = local.split('T');
  const [hourRaw, minuteRaw] = (timePart || '').split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  const floored = `${datePart}T${String(hour).padStart(2, '0')}:${String(Math.floor(minute / 15) * 15).padStart(2, '0')}`;
  return parseSiteDateTimeLocalInput(floored, timeZone) || null;
}

const PT15M_MS = 15 * 60 * 1000;

/**
 * Instant the next site-local PT15M wall-clock window opens.
 * Adds 15 elapsed minutes to the current open window start (DST-safe).
 */
export function nextOpenSitePt15mWindowStart(
  asOf: string | Date | null | undefined,
  timeZone?: string | null,
): string | null {
  const current = openSitePt15mWindowStart(asOf, timeZone);
  if (!current) return null;
  const startMs = Date.parse(current);
  if (!Number.isFinite(startMs)) return null;
  return new Date(startMs + PT15M_MS).toISOString();
}

/** Milliseconds until the next site-local PT15M wall-clock boundary. */
export function msUntilNextOpenSitePt15mBoundary(
  asOf: string | Date | null | undefined,
  timeZone?: string | null,
): number | null {
  const next = nextOpenSitePt15mWindowStart(asOf, timeZone);
  if (!next) return null;
  const asOfMs = asOf instanceof Date ? asOf.getTime() : Date.parse(String(asOf || ''));
  if (!Number.isFinite(asOfMs)) return null;
  return Math.max(0, Date.parse(next) - asOfMs);
}
